import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HTTPService } from 'src/app/services/http.service';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { SnackbarService } from 'src/app/services/snackbar.service';
import {
  ScreenLauncher,
  CreateLauncherDto,
  UpdateLauncherDto,
  LauncherApiResponse,
  LauncherFilters
} from '../interfaces/launcher.models';

@Injectable({
  providedIn: 'root'
})
export class LauncherApiService {
  
  constructor(
    private http: HTTPService,
    private uas: UserAuthenticationServices,
    private snackbar: SnackbarService
  ) {}

  /**
   * Cria launcher de tela usando stored procedure
   */
  createLauncher(launcher: CreateLauncherDto): Observable<number> {
    const codecompany = this.uas.user.codecompany;
    const codecontract = this.uas.user.companylist && this.uas.user.companylist.length > 0 
      ? this.uas.user.companylist[0].codecontract 
      : null;
    const iduser = this.uas.user.iduser;
    const username = this.uas.user.username;

    // Detectar se é ícone customizado (começa com "custom:")
    let iconName = launcher.iconName;
    let svgIconName = launcher.svgIconName;
    
    if (iconName && iconName.startsWith('custom:')) {
      // É um ícone SVG customizado
      svgIconName = iconName; // Mantém "custom:nome"
      iconName = null; // Não usa iconName padrão
    }

    const payload = {
      IDTEMPLATE: launcher.idTemplate,
      NAME: launcher.name,
      DESCRIPTION: launcher.description,
      ROUTEPATH: this.sanitizeRoutePath(launcher.routePath),
      CODECOMPANY: codecompany,
      CODECONTRACT: codecontract,
      ICONNAME: iconName,
      SVGICONNAME: svgIconName,
      PARENTMENUID: launcher.parentMenuId,
      MENUORDER: launcher.menuOrder || 0,
      DATASOURCECONFIG: launcher.dataSourceConfig 
        ? JSON.stringify(launcher.dataSourceConfig) 
        : '{"type":"none"}',
      IDUSER: iduser,
      CREATEDBY: username
    };

    return this.http.post(payload, 'ScreenLauncherServices/create', true, true, true).pipe(
      map((response: LauncherApiResponse) => {
        if (response && response.IDLAUNCHER) {
          this.snackbar.showSuccess(response.MESSAGE || 'Lançador criado com sucesso!');
          return response.IDLAUNCHER;
        }
        throw new Error(response?.MESSAGE || 'Erro ao criar lançador');
      }),
      catchError((error) => {
        console.error('Erro ao criar launcher:', error);
        this.snackbar.showError(error?.MESSAGE || 'Erro ao criar lançador de tela');
        return throwError(() => error);
      })
    );
  }

  /**
   * Remove launcher (soft delete) usando stored procedure
   */
  deleteLauncher(idLauncher: number): Observable<boolean> {
    const username = this.uas.user.username;

    const payload = {
      IDLAUNCHER: idLauncher,
      DELETEDBY: username
    };

    return this.http.post(payload, 'ScreenLauncherServices/delete', true, true, true).pipe(
      map((response: LauncherApiResponse) => {
        this.snackbar.showSuccess(response.MESSAGE || 'Lançador removido com sucesso!');
        return true;
      }),
      catchError((error) => {
        console.error('Erro ao remover launcher:', error);
        this.snackbar.showError(error?.MESSAGE || 'Erro ao remover lançador');
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista launchers da empresa
   */
  getLaunchers(filters?: LauncherFilters): Observable<ScreenLauncher[]> {
    const codecompany = filters?.codecompany || this.uas.user.codecompany;

    let condition = `CODECOMPANY = ${codecompany} AND ISDELETED = 0`;

    if (filters?.idTemplate) {
      condition += ` AND IDTEMPLATE = ${filters.idTemplate}`;
    }

    if (filters?.isActive !== undefined) {
      condition += ` AND ISACTIVE = ${filters.isActive ? 1 : 0}`;
    }

    if (filters?.searchText && filters.searchText.trim()) {
      condition += ` AND (NAME LIKE '%${filters.searchText}%' OR DESCRIPTION LIKE '%${filters.searchText}%')`;
    }

    const query = {
      entityName: 'Mktpscreenlauncher',
      sql: `SELECT * FROM MKTPSCREENLAUNCHER 
            WHERE ${condition}
            ORDER BY CREATEDON DESC`
    };

    return this.http.post(query, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: any) => {
        if (response && response.SQL) {
          return response.SQL.map((item: any) => this.mapFromApi(item));
        }
        return [];
      }),
      catchError((error) => {
        console.error('Erro ao listar launchers:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém launcher por ID
   */
  getLauncherById(idLauncher: number): Observable<ScreenLauncher> {
    const query = {
      entityName: 'Mktpscreenlauncher',
      sql: `SELECT * FROM MKTPSCREENLAUNCHER 
            WHERE IDLAUNCHER = ${idLauncher} AND ISDELETED = 0`
    };

    return this.http.post(query, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: any) => {
        if (response && response.SQL && response.SQL.length > 0) {
          return this.mapFromApi(response.SQL[0]);
        }
        throw new Error('Launcher não encontrado');
      })
    );
  }

  /**
   * Atualiza launcher existente
   */
  updateLauncher(idLauncher: number, updates: UpdateLauncherDto): Observable<boolean> {
    const username = this.uas.user.username;

    const ob = {
      entityName: 'Mktpscreenlauncher',
      IDLAUNCHER: idLauncher,
      ...updates,
      MODIFIEDBY: username,
      MODIFIEDON: new Date()
    };

    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => {
        this.snackbar.showSuccess('Launcher atualizado com sucesso!');
        return true;
      }),
      catchError((error) => {
        console.error('Erro ao atualizar launcher:', error);
        this.snackbar.showError('Erro ao atualizar lançador');
        return throwError(() => error);
      })
    );
  }

  /**
   * Sanitiza routePath para formato válido
   * Remove acentos, espaços, caracteres especiais
   */
  private sanitizeRoutePath(path: string): string {
    return path
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]/g, '-') // Substitui especiais por hífen
      .replace(/-+/g, '-') // Remove hífens duplicados
      .replace(/^-|-$/g, ''); // Remove hífens início/fim
  }

  /**
   * Mapeia dados da API para modelo TypeScript
   */
  private mapFromApi(apiData: any): ScreenLauncher {
    // Parseia dataSourceConfig se existir
    let dataSourceConfig;
    if (apiData.DATASOURCECONFIG) {
      try {
        dataSourceConfig = JSON.parse(apiData.DATASOURCECONFIG);
      } catch (error) {
        console.error('[LauncherApiService] Erro ao parsear DATASOURCECONFIG:', error);
        dataSourceConfig = { type: 'none' };
      }
    } else {
      dataSourceConfig = { type: 'none' };
    }

    return {
      idLauncher: apiData.IDLAUNCHER,
      idTemplate: apiData.IDTEMPLATE,
      launcherKey: apiData.LAUNCHERKEY,
      name: apiData.NAME,
      description: apiData.DESCRIPTION,
      routePath: apiData.ROUTEPATH,
      codecompany: apiData.CODECOMPANY,
      iconName: apiData.ICONNAME,
      svgIconName: apiData.SVGICONNAME,
      parentMenuId: apiData.PARENTMENUID,
      menuOrder: apiData.MENUORDER,
      isActive: apiData.ISACTIVE === 1,
      dataSourceConfig: dataSourceConfig,
      idUser: apiData.IDUSER,
      createdBy: apiData.CREATEDBY,
      createdOn: apiData.CREATEDON ? new Date(apiData.CREATEDON) : undefined,
      modifiedBy: apiData.MODIFIEDBY,
      modifiedOn: apiData.MODIFIEDON ? new Date(apiData.MODIFIEDON) : undefined,
      isDeleted: apiData.ISDELETED === 1,
      deletedBy: apiData.DELETEDBY,
      deletedOn: apiData.DELETEDON ? new Date(apiData.DELETEDON) : undefined
    };
  }
}
